import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get category from URL, default to 'all'
  const selectedCategory = searchParams.get('category') || 'all';

  useEffect(() => {
    // Scroll to top when shop page loads
    window.scrollTo(0, 0);
    fetchProducts();
  }, [selectedCategory]);

  const fetchProducts = async () => {
    try {
      const url = selectedCategory === 'all' 
        ? `${API}/products`
        : `${API}/products?category=${selectedCategory}`;
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (cat) => {
    // Update URL with category - this preserves it for back navigation
    if (cat === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category: cat });
    }
  };

  const categories = ['all', 'plants', 'bundles', 'fish', 'invertebrates', 'hardscape'];

  return (
    <div className="min-h-screen py-12 md:py-16" data-testid="shop-page">
      <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl">
        <div className="mb-8 md:mb-12">
          <h1 className="heading-font text-4xl sm:text-5xl md:text-6xl font-bold mb-4" data-testid="shop-title">Shop All</h1>
          <p className="text-base md:text-lg text-muted-foreground">Explore our full collection of aquarium plants and livestock</p>
        </div>

        <div className="flex gap-2 mb-6 md:mb-8 flex-wrap" data-testid="category-filters">
          {categories.map((cat) => (
            <Button
              key={cat}
              onClick={() => handleCategoryChange(cat)}
              variant={selectedCategory === cat ? 'default' : 'outline'}
              className={`rounded-full focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 ${
                selectedCategory !== cat ? 'bg-transparent border-border text-foreground hover:bg-secondary focus:bg-transparent focus:text-foreground' : ''
              }`}
              data-testid={`category-${cat}`}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No products found</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6" data-testid="products-grid">
            {products.map((product) => (
              <ProductCard key={product._id} product={{ ...product, id: product._id }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Shop;
